// Complete global-only /lpn-pages API export supplied on 2026-10-01; attribute values are unchanged.
import GLOBAL_LPN_SOURCE from './global-lpn-snapshot.json';

type GlobalLpnSource = (typeof GLOBAL_LPN_SOURCE)[number];

const KNOWN_FILTER_VALUES = new Set([
  'Online',
  'In-Person',
  'Hybrid',
  'Self-Paced',
  'Spanish',
  'Associate',
  "Bachelor's",
  'Boot Camp',
  'Certificate',
  'Doctoral',
  'GED',
  'HS Diploma',
  "Master's",
  'Short Form Course',
  'Accounting',
  'AI & Machine',
  'Applied Sciences',
  'Business',
  'CFP®',
  'Computer Science',
  'Construction Mgmt',
  'Cybersecurity',
  'Data Science',
  'Education',
  'Executive Education',
  'Healthcare',
  'Human Resources',
  'Integrative Health',
  'Nursing',
  'Project Mgmt/Six Sigma',
  'Psychology',
  'Public Safety',
  'Security & Global Studies',
  'Social Sciences',
  'STEM',
]);

function selectedValues(params: URLSearchParams, key: string) {
  return (params.get(key) ?? '')
    .split(',')
    .map((value) => value.trim())
    .filter((value) => KNOWN_FILTER_VALUES.has(value));
}

function includesAny(values: readonly string[], selected: readonly string[]) {
  return !selected.length || selected.some((value) => values.includes(value));
}

function matchesSavings(page: GlobalLpnSource, selected: readonly string[]) {
  return selected.every((value) => {
    switch (value) {
      case 'capped_tuition':
        return page.badgeCappedTuition;
      case 'family_eligible':
        return page.badgeFamilySavings;
      case 'no_fees':
        return page.badgeFeesWaived;
      default:
        return true;
    }
  });
}

function toResource(page: GlobalLpnSource) {
  const { id, ...attributes } = page;
  return {
    id,
    type: 'lpn-pages',
    attributes,
    relationships: {
      companyLpnSettings: { data: [] },
    },
  };
}

/** Builds the global Learning Partner Network response with live route filtering and pagination. */
export function buildDemoGlobalLpnPagesDoc(url: string) {
  const params = new URL(url).searchParams;
  if (params.has('filter[employer-recommended]')) {
    return { data: [], meta: { page: { total: 0 } } };
  }

  const search = (params.get('filter[search]') ?? '').trim().toLocaleLowerCase();
  const format = selectedValues(params, 'filter[format]');
  const programs = selectedValues(params, 'filter[programs]');
  const areasOfStudy = selectedValues(params, 'filter[areas-of-study]');
  const savings = (params.get('filter[savings]') ?? '')
    .split(',')
    .map((value) => value.trim())
    .filter((value) => ['capped_tuition', 'family_eligible', 'no_fees'].includes(value));

  const filtered = GLOBAL_LPN_SOURCE.filter((page) => {
    const searchable = page.schoolName.toLocaleLowerCase();
    return (
      (!search || searchable.includes(search)) &&
      includesAny(page.format, format) &&
      includesAny(page.programs, programs) &&
      includesAny(page.areasOfStudy, areasOfStudy) &&
      matchesSavings(page, savings)
    );
  }).sort((left, right) => left.schoolName.localeCompare(right.schoolName));

  const offset = Math.max(0, Number(params.get('page[offset]')) || 0);
  const limit = Math.max(0, Number(params.get('page[limit]')) || 50);
  return {
    data: filtered.slice(offset, offset + limit).map(toResource),
    meta: { page: { total: filtered.length } },
  };
}
